import { useEffect, useState } from 'react';
import { useRealtimeSubscription } from './useRealtimeSubscription';
import { useUser } from '../useUser';
import { supabase } from '../../lib/supabase';
import { DashboardMetric, StatusItem, ActivityItem } from '../../types/dashboard';

/**
 * Base hook for all dashboard metrics that depend on user role
 * @returns An object containing all dashboard metrics for the current user
 */
export function useDashboardMetrics() {
  const { user } = useUser();
  const [dashboardData, setDashboardData] = useState<{
    metrics: DashboardMetric[];
    statuses: StatusItem[];
    activities: ActivityItem[];
    isLoading: boolean;
    error: Error | null;
  }>({
    metrics: [],
    statuses: [],
    activities: [],
    isLoading: true,
    error: null
  });

  useEffect(() => {
    if (!user) return;

    const loadDashboardData = async () => {
      try {
        // Use different queries depending on user role to get relevant metrics
        let metrics: DashboardMetric[] = [];
        const statuses: StatusItem[] = [];
        const activities: ActivityItem[] = [];
        
        // Common metrics across all users
        const { count: projectsCount, error: projectsError } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true });
          
        if (projectsError) throw projectsError;
        
        metrics.push({
          title: 'Total Projects',
          value: projectsCount || 0,
          icon: '📁',
          colorScheme: 'primary'
        });

        // Role-specific metrics
        if (user.role === 'Admin' || user.role === 'SuperAdmin' || user.role === 'Owner') {
          // Admin metrics - total users, departments, etc.
          const { count: usersCount, error: usersError } = await supabase
            .from('users')
            .select('*', { count: 'exact', head: true })
            .is('deleted_at', null);
            
          if (usersError) throw usersError;
          
          metrics.push({
            title: 'Active Users',
            value: usersCount || 0,
            icon: '👥',
            colorScheme: 'secondary'
          });
          
          // Add departments metric for admins
          const { count: departmentsCount, error: deptsError } = await supabase
            .from('departments')
            .select('*', { count: 'exact', head: true })
            .is('deleted_at', null);
            
          if (deptsError) throw deptsError;
          
          metrics.push({
            title: 'Departments',
            value: departmentsCount || 0,
            icon: '🏢',
            colorScheme: 'accent'
          });
        }
        
        if (user.role === 'DesignManager' || user.role === 'Admin' || user.role === 'SuperAdmin' || user.role === 'Owner') {
          // Design-related metrics
          const { count: designsCount, error: designsError } = await supabase
            .from('designs')
            .select('*', { count: 'exact', head: true });
            
          if (designsError) throw designsError;
          
          metrics.push({
            title: 'Design Files',
            value: designsCount || 0,
            icon: '📝',
            colorScheme: 'success'
          });
          
          // BOM metrics
          const { count: bomsCount, error: bomsError } = await supabase
            .from('boms')
            .select('*', { count: 'exact', head: true });
            
          if (bomsError) throw bomsError;
          
          metrics.push({
            title: 'BOMs',
            value: bomsCount || 0,
            icon: '📋',
            colorScheme: 'primary'
          });
        }
        
        if (user.role === 'ProductionManager' || user.role === 'Admin' || user.role === 'SuperAdmin' || user.role === 'Owner') {
          // Production-related metrics
          const { count: workOrdersCount, error: woError } = await supabase
            .from('work_orders')
            .select('*', { count: 'exact', head: true });
            
          if (woError) throw woError;
          
          metrics.push({
            title: 'Work Orders',
            value: workOrdersCount || 0,
            icon: '🔧',
            colorScheme: 'warning'
          });
          
          // Job cards metrics
          const { count: jobCardsCount, error: jcError } = await supabase
            .from('job_cards')
            .select('*', { count: 'exact', head: true });
            
          if (jcError) throw jcError;
          
          metrics.push({
            title: 'Job Cards',
            value: jobCardsCount || 0,
            icon: '📇',
            colorScheme: 'accent'
          });
        }
        
        if (user.role === 'InventoryManager' || user.role === 'Admin' || user.role === 'SuperAdmin' || user.role === 'Owner') {
          // Inventory-related metrics
          const { count: inventoryCount, error: invError } = await supabase
            .from('inventory_items')
            .select('*', { count: 'exact', head: true });
            
          if (invError) throw invError;
          
          metrics.push({
            title: 'Inventory Items',
            value: inventoryCount || 0,
            icon: '📦',
            colorScheme: 'primary'
          });
        }
        
        if (user.role === 'ProcurementOfficer' || user.role === 'Admin' || user.role === 'SuperAdmin' || user.role === 'Owner') {
          // Procurement-related metrics
          const { count: poCount, error: poError } = await supabase
            .from('purchase_orders')
            .select('*', { count: 'exact', head: true });
            
          if (poError) throw poError;
          
          metrics.push({
            title: 'Purchase Orders',
            value: poCount || 0,
            icon: '🛒',
            colorScheme: 'success'
          });
          
          // MRP metrics
          const { count: mrpCount, error: mrpError } = await supabase
            .from('mrps')
            .select('*', { count: 'exact', head: true });
            
          if (mrpError) throw mrpError;
          
          metrics.push({
            title: 'MRP Plans',
            value: mrpCount || 0,
            icon: '📊',
            colorScheme: 'warning'
          });
        }
        
        if (user.role === 'ProjectManager' || user.role === 'Admin' || user.role === 'SuperAdmin' || user.role === 'Owner') {
          // Project-related metrics
          const { count: stagesCount, error: stagesError } = await supabase
            .from('stages')
            .select('*', { count: 'exact', head: true });
            
          if (stagesError) throw stagesError;
          
          metrics.push({
            title: 'Project Stages',
            value: stagesCount || 0,
            icon: '🔄',
            colorScheme: 'secondary'
          });
        }

        // For workers, show only their assigned tasks and job cards
        if (user.role === 'Worker') {
          const { count: taskCount, error: taskError } = await supabase
            .from('job_cards')
            .select('*', { count: 'exact', head: true })
            .eq('worker_id', user.id);
            
          if (taskError) throw taskError;
          
          metrics.push({
            title: 'My Job Cards',
            value: taskCount || 0,
            icon: '📋',
            colorScheme: 'primary'
          });
          
          // Get active/in-progress job cards for the worker
          const { count: activeCount, error: activeError } = await supabase
            .from('job_cards')
            .select('*', { count: 'exact', head: true })
            .eq('worker_id', user.id)
            .eq('status', 'in_progress');
            
          if (activeError) throw activeError;
          
          metrics.push({
            title: 'Active Jobs',
            value: activeCount || 0,
            icon: '⚙️',
            colorScheme: 'warning'
          });
        }

        // Load recent activities for all users
        const { data: recentActivities, error: activitiesError } = await supabase
          .from('project_activities')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(5);
          
        if (activitiesError) throw activitiesError;
        
        setDashboardData({
          metrics,
          statuses,
          activities: recentActivities || [],
          isLoading: false,
          error: null
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setDashboardData(prev => ({
          ...prev,
          isLoading: false,
          error: error as Error
        }));
      }
    };

    loadDashboardData();
  }, [user]);

  return dashboardData;
}
